import { useEffect, useState, useCallback } from 'react';
import { Card, Typography, Row, Col, Statistic, Spin, Select, Empty, Tag, Button } from 'antd';
import {
  CarOutlined,
  UserOutlined,
  FileTextOutlined,
  AlertOutlined,
  ReloadOutlined,
  CalendarOutlined,
  ThunderboltOutlined,
  AimOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer
} from 'recharts';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  supabase,
  formatDate,
  fetchExpiringCredentials,
  fetchVehicleAvailability,
  summarizeVehicleAvailability,
  fetchLatestVehiclePositions,
  countOnlineVehicles,
  useAuthUser,
  useFleetOperator,
  parseUserRole,
  isAdminRole,
  isFleetOwnerRole,
  civilDateInTimeZone,
  type FleetVehiclePosition,
} from '@ubus/shared';
import PageHeader from '../components/PageHeader';
import { FleetMapPanel } from '../components/FleetMapPanel';
import { resolveAdminBreadcrumbs } from '../utils/adminBreadcrumbs';
import { chartColors } from '../theme/adminTheme';

const { Text, Link } = Typography;

const CHART_COLORS = chartColors;

const PIE_COLORS = [
  CHART_COLORS.primary,
  CHART_COLORS.success,
  CHART_COLORS.warning,
  CHART_COLORS.error,
  CHART_COLORS.info,
];

interface WeeklyTripData {
  date: string;
  scheduled: number;
  completed: number;
  cancelled: number;
}

interface VehicleStatusData {
  name: string;
  value: number;
}

interface TripStatusData {
  name: string;
  value: number;
}

interface ExpiringDriver {
  id: string;
  name: string;
  license_expiry: string;
}

interface ExpiringVehicle {
  id: string;
  fleet_number: string;
  registration_expiry: string;
}

function daysUntilExpiry(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(dateStr);
  expiry.setHours(0, 0, 0, 0);
  return Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function expiryTagColor(days: number): string {
  if (days <= 7) return 'red';
  if (days <= 30) return 'orange';
  return 'default';
}

function Dashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const breadcrumbs = resolveAdminBreadcrumbs(location.pathname, t);
  void i18n.language;
  const user = useAuthUser();
  const role = parseUserRole(user);
  const isHq = isAdminRole(role);
  const isOwner = isFleetOwnerRole(role);
  const { operatorId, loading: operatorLoading } = useFleetOperator();
  const [stats, setStats] = useState({
    totalVehicles: 0,
    activeDrivers: 0,
    todayTrips: 0,
    sosEvents: 0
  });
  const [fleetPositions, setFleetPositions] = useState<FleetVehiclePosition[]>([]);
  const [fleetMapLoading, setFleetMapLoading] = useState(true);
  const [vehiclesOnline, setVehiclesOnline] = useState(0);
  const [tomorrowUnscheduled, setTomorrowUnscheduled] = useState(0);
  const [overdueUnscheduled, setOverdueUnscheduled] = useState(0);
  const [pendingOwnerOrders, setPendingOwnerOrders] = useState(0);
  const [weeklyTrips, setWeeklyTrips] = useState<WeeklyTripData[]>([]);
  const [vehicleStatus, setVehicleStatus] = useState<VehicleStatusData[]>([]);
  const [tripStatus, setTripStatus] = useState<TripStatusData[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<7 | 30>(7);
  const [expiringDrivers, setExpiringDrivers] = useState<ExpiringDriver[]>([]);
  const [expiringVehicles, setExpiringVehicles] = useState<ExpiringVehicle[]>([]);
  const [availabilityToday, setAvailabilityToday] = useState({
    available: 0,
    busy: 0,
    pending_decision: 0,
    maintenance: 0,
  });

  const loadFleetMap = useCallback(async () => {
    if (isOwner && (operatorLoading || !operatorId)) {
      if (!operatorLoading && !operatorId) {
        setFleetPositions([]);
        setVehiclesOnline(0);
        setFleetMapLoading(false);
      }
      return;
    }
    setFleetMapLoading(true);
    try {
      const rows = await fetchLatestVehiclePositions({
        fleetOperatorId: isOwner ? operatorId : null,
      });
      setFleetPositions(rows);
      setVehiclesOnline(countOnlineVehicles(rows));
    } catch (err) {
      console.error('Failed to fetch fleet positions:', err);
      setFleetPositions([]);
      setVehiclesOnline(0);
    } finally {
      setFleetMapLoading(false);
    }
  }, [isOwner, operatorId, operatorLoading]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchStats(),
        fetchWeeklyTrips(),
        fetchVehicleStatus(),
        fetchTripStatus(),
        fetchAvailabilityToday(),
        loadFleetMap(),
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [timeRange, t, loadFleetMap]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Poll fleet map every 30s (Realtime throttle fallback)
  useEffect(() => {
    const id = window.setInterval(() => {
      void loadFleetMap();
    }, 30_000);
    return () => window.clearInterval(id);
  }, [loadFleetMap]);

  // Lightweight Realtime: refresh map when location_logs insert
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const channel = supabase
      .channel('dashboard-fleet-gps')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'location_logs' },
        () => {
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => {
            void loadFleetMap();
          }, 5000);
        }
      )
      .subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      void supabase.removeChannel(channel);
    };
  }, [loadFleetMap]);

  useEffect(() => {
    fetchExpiringCredentials(30)
      .then(({ drivers, vehicles }) => {
        setExpiringDrivers(drivers as ExpiringDriver[]);
        setExpiringVehicles(vehicles as ExpiringVehicle[]);
      })
      .catch((err) => console.error('Failed to fetch expiring credentials:', err));
  }, []);

  useEffect(() => {
    if (isHq) {
      const todayStr = civilDateInTimeZone();
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];

      void supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('schedule_date', tomorrowStr)
        .in('status', ['unscheduled', 'planning'])
        .neq('assist_status', 'pending_platform')
        .then(({ count, error }) => {
          if (!error && count !== null) setTomorrowUnscheduled(count);
        });

      void supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .lte('schedule_date', todayStr)
        .in('status', ['unscheduled', 'planning'])
        .neq('assist_status', 'pending_platform')
        .then(({ count, error }) => {
          if (!error && count !== null) setOverdueUnscheduled(count);
        });
    }

    if (isOwner) {
      supabase
        .from('trips')
        .select('*', { count: 'exact', head: true })
        .eq('assignment_status', 'pending_owner')
        .then(({ count, error }) => {
          if (!error && count !== null) setPendingOwnerOrders(count);
        });
    }
  }, [isHq, isOwner]);

  const fetchAvailabilityToday = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const rows = await fetchVehicleAvailability({ date: today });
      const summary = summarizeVehicleAvailability(rows);
      setAvailabilityToday({
        available: summary.available,
        busy: summary.busy,
        pending_decision: summary.pending_decision,
        maintenance: summary.maintenance,
      });
    } catch (err) {
      console.error('Failed to fetch fleet availability:', err);
    }
  };

  // 获取基本统计
  const fetchStats = async () => {
    const [
      { count: vehicleCount },
      { count: driverCount },
      { count: tripCount },
      { count: sosCount }
    ] = await Promise.all([
      supabase.from('vehicles').select('*', { count: 'exact', head: true }),
      supabase.from('drivers').select('*', { count: 'exact', head: true }).eq('status', 'active'),
      supabase.from('trips').select('*', { count: 'exact', head: true }).eq('date', new Date().toISOString().split('T')[0]),
      supabase.from('sos_events').select('*', { count: 'exact', head: true }).eq('status', 'new')
    ]);

    setStats({
      totalVehicles: vehicleCount || 0,
      activeDrivers: driverCount || 0,
      todayTrips: tripCount || 0,
      sosEvents: sosCount || 0
    });
  };

  // 获取周度/月度行程趋势
  const fetchWeeklyTrips = async () => {
    const today = new Date();
    const days: WeeklyTripData[] = [];

    for (let i = timeRange - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const displayDate = `${date.getMonth() + 1}/${date.getDate()}`;
      
      // 获取当天各状态的行程数
      const [{ count: scheduled }, { count: completed }, { count: cancelled }] = await Promise.all([
        supabase.from('trips').select('*', { count: 'exact', head: true }).eq('date', dateStr).eq('status', 'scheduled'),
        supabase.from('trips').select('*', { count: 'exact', head: true }).eq('date', dateStr).eq('status', 'completed'),
        supabase.from('trips').select('*', { count: 'exact', head: true }).eq('date', dateStr).eq('status', 'cancelled')
      ]);

      days.push({
        date: displayDate,
        scheduled: scheduled || 0,
        completed: completed || 0,
        cancelled: cancelled || 0
      });
    }

    setWeeklyTrips(days);
  };

  // 获取车辆状态分布
  const fetchVehicleStatus = async () => {
    const statuses = ['active', 'maintenance', 'retired'] as const;
    const results: VehicleStatusData[] = [];

    for (const status of statuses) {
      const { count } = await supabase
        .from('vehicles')
        .select('*', { count: 'exact', head: true })
        .eq('status', status);
      
      results.push({
        name: status === 'active' ? t('dashboard.active') : status === 'maintenance' ? t('dashboard.maintenance') : t('dashboard.retired'),
        value: count || 0
      });
    }

    setVehicleStatus(results);
  };

  // 获取行程状态分布（本周）
  const fetchTripStatus = async () => {
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    
    const statuses = ['scheduled', 'dispatched', 'in_progress', 'completed', 'cancelled'] as const;
    const results: TripStatusData[] = [];

    for (const status of statuses) {
      const { count } = await supabase
        .from('trips')
        .select('*', { count: 'exact', head: true })
        .eq('status', status)
        .gte('date', weekAgo.toISOString().split('T')[0]);
      
      const nameMap: Record<string, string> = {
        scheduled: t('dashboard.scheduled'),
        dispatched: t('dashboard.dispatched'),
        in_progress: t('dashboard.inProgress'),
        completed: t('dashboard.completed'),
        cancelled: t('dashboard.cancelled')
      };
      
      results.push({
        name: nameMap[status],
        value: count || 0
      });
    }

    setTripStatus(results.filter(r => r.value > 0));
  };

  // 自定义 Tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Card size="small" style={{ border: '1px solid #f0f0f0' }}>
          <Text strong>{label}</Text>
          {payload.map((p: any, idx: number) => (
            <div key={idx} style={{ color: p.color, marginTop: 4 }}>
              {p.name}: {p.value}
            </div>
          ))}
        </Card>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="admin-page--fluid admin-page--density-ops">
      <PageHeader
        title={t('dashboard.title')}
        subtitle={t(isOwner ? 'dashboard.subtitleOwner' : 'dashboard.subtitleHq')}
        breadcrumbs={breadcrumbs}
        extra={
          <Select
            value={timeRange}
            onChange={(val) => setTimeRange(val)}
            style={{ width: 160 }}
            options={[
              { label: t('dashboard.last7Days'), value: 7 },
              { label: t('dashboard.last30Days'), value: 30 },
            ]}
            suffixIcon={
              <ReloadOutlined
                style={{ cursor: 'pointer', color: CHART_COLORS.primary }}
                onClick={fetchAllData}
              />
            }
          />
        }
      />

      <Row gutter={[16, 16]} className="admin-action-queue">
        {isHq ? (
          <>
            <Col xs={24} sm={12} lg={6} xl={6} xxl={6}>
              <Card
                hoverable
                className={`admin-action-card${stats.sosEvents > 0 ? ' admin-action-card--danger' : ''}`}
                onClick={() => navigate('/sos-events')}
              >
                <Statistic title={t('dashboard.openSos')} value={stats.sosEvents} prefix={<AlertOutlined />} />
                <Button type="link" style={{ paddingLeft: 0 }}>
                  {t('dashboard.handleAlerts')}
                </Button>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6} xl={6} xxl={6}>
              <Card
                hoverable
                className={`admin-action-card${overdueUnscheduled > 0 ? ' admin-action-card--danger' : ''}`}
                onClick={() => navigate(`/jobs?date=${civilDateInTimeZone()}`)}
              >
                <Statistic
                  title={t('dashboard.overdueUnscheduled')}
                  value={overdueUnscheduled}
                  prefix={<WarningOutlined />}
                />
                <Button type="link" style={{ paddingLeft: 0 }}>
                  {t('dashboard.openJobs')}
                </Button>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6} xl={6} xxl={6}>
              <Card
                hoverable
                className={`admin-action-card${tomorrowUnscheduled > 0 ? ' admin-action-card--warn' : ''}`}
                onClick={() => {
                  const d = new Date();
                  d.setDate(d.getDate() + 1);
                  navigate(`/jobs?date=${d.toISOString().split('T')[0]}`);
                }}
              >
                <Statistic
                  title={t('dashboard.tomorrowUnscheduled')}
                  value={tomorrowUnscheduled}
                  prefix={<CalendarOutlined />}
                />
                <Button type="link" style={{ paddingLeft: 0 }}>
                  {t('dashboard.openJobs')}
                </Button>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6} xl={6} xxl={6}>
              <Card
                hoverable
                className={`admin-action-card${expiringDrivers.length + expiringVehicles.length > 0 ? ' admin-action-card--warn' : ''}`}
                onClick={() => navigate('/drivers')}
              >
                <Statistic
                  title={t('dashboard.expiringCredentials')}
                  value={expiringDrivers.length + expiringVehicles.length}
                  prefix={<FileTextOutlined />}
                />
                <Button type="link" style={{ paddingLeft: 0 }}>
                  {t('dashboard.review')}
                </Button>
              </Card>
            </Col>
          </>
        ) : (
          <>
            <Col xs={24} sm={12} lg={8} xl={8} xxl={8}>
              <Card
                hoverable
                className={`admin-action-card${pendingOwnerOrders > 0 ? ' admin-action-card--warn' : ''}`}
                onClick={() => navigate('/jobs')}
              >
                <Statistic
                  title={t('dashboard.myJobs')}
                  value={pendingOwnerOrders > 0 ? pendingOwnerOrders : t('dashboard.go')}
                  prefix={<FileTextOutlined />}
                />
                <Button type="link" style={{ paddingLeft: 0 }}>
                  {pendingOwnerOrders > 0
                    ? t('dashboard.reviewOrders')
                    : t('dashboard.openMyJobs')}
                </Button>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={8} xxl={8}>
              <Card hoverable className="admin-action-card" onClick={() => navigate('/daily-schedule')}>
                <Statistic title={t('dashboard.dailySchedule')} value={t('dashboard.go')} prefix={<ThunderboltOutlined />} />
                <Button type="link" style={{ paddingLeft: 0 }}>
                  {t('dashboard.generatePublish')}
                </Button>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={8} xxl={8}>
              <Card hoverable className="admin-action-card" onClick={() => navigate('/trips')}>
                <Statistic title={t('dashboard.myTrips')} value={t('dashboard.go')} prefix={<CalendarOutlined />} />
                <Button type="link" style={{ paddingLeft: 0 }}>
                  {t('dashboard.openTrips')}
                </Button>
              </Card>
            </Col>
          </>
        )}
      </Row>

      <div style={{ marginTop: 16 }}>
        <FleetMapPanel
          positions={fleetPositions}
          loading={fleetMapLoading || (isOwner && operatorLoading)}
          title={t(isOwner ? 'dashboard.fleetMapTitleOwner' : 'dashboard.fleetMapTitle')}
          subtitle={t('dashboard.fleetMapSubtitle')}
        />
      </div>

      {/* KPI cards */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} md={6} xl={6} xxl={6}>
          <Card hoverable className="admin-stat-card">
            <Statistic
              title={<Text type="secondary">{t('dashboard.vehiclesOnline')}</Text>}
              value={vehiclesOnline}
              suffix={<Text type="secondary" style={{ fontSize: 16 }}>/ {stats.totalVehicles}</Text>}
              prefix={<AimOutlined style={{ color: CHART_COLORS.success, fontSize: 24 }} />}
              valueStyle={{ color: CHART_COLORS.success, fontSize: 32 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} xl={6} xxl={6}>
          <Card hoverable className="admin-stat-card">
            <Statistic
              title={<Text type="secondary">{t('dashboard.totalVehicles')}</Text>}
              value={stats.totalVehicles}
              prefix={<CarOutlined style={{ color: CHART_COLORS.primary, fontSize: 24 }} />}
              valueStyle={{ color: CHART_COLORS.primary, fontSize: 32 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} xl={6} xxl={6}>
          <Card hoverable className="admin-stat-card">
            <Statistic
              title={<Text type="secondary">{t('dashboard.activeDrivers')}</Text>}
              value={stats.activeDrivers}
              prefix={<UserOutlined style={{ color: CHART_COLORS.success, fontSize: 24 }} />}
              valueStyle={{ color: CHART_COLORS.success, fontSize: 32 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} xl={6} xxl={6}>
          <Card
            hoverable
            className="admin-stat-card"
            onClick={() => navigate('/fleet-availability')}
          >
            <Statistic
              title={<Text type="secondary">{t('dashboard.availableToday')}</Text>}
              value={availabilityToday.available}
              prefix={<CarOutlined style={{ color: CHART_COLORS.success, fontSize: 24 }} />}
              valueStyle={{ color: CHART_COLORS.success, fontSize: 32 }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card
            hoverable
            className="admin-stat-card"
            onClick={() => navigate('/fleet-availability')}
            style={{ cursor: 'pointer' }}
          >
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Text type="secondary">{t('dashboard.fleetAvailability')}</Text>
                <div style={{ marginTop: 8 }}>
                  <Tag color="success">{t('dashboard.available', { count: availabilityToday.available })}</Tag>
                  <Tag color="processing">{t('dashboard.busy', { count: availabilityToday.busy })}</Tag>
                  <Tag color="gold">{t('dashboard.pendingOwner', { count: availabilityToday.pending_decision })}</Tag>
                  <Tag color="warning">{t('dashboard.maintenance', { count: availabilityToday.maintenance })}</Tag>
                </div>
              </Col>
              <Col>
                <Link>{t('dashboard.viewDetails')}</Link>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Credential Expiry Alerts */}
      <Card
        title={t('dashboard.credentialAlerts')}
        style={{ marginTop: 16, borderRadius: 12 }}
      >
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <Text strong>
              {t('dashboard.drivers')}{' '}
              <Link onClick={() => navigate('/drivers')} style={{ fontSize: 12 }}>
                {t('dashboard.viewAll')}
              </Link>
            </Text>
            {expiringDrivers.length > 0 ? (
              <div style={{ marginTop: 8 }}>
                {expiringDrivers.map((d) => {
                  const days = daysUntilExpiry(d.license_expiry);
                  return (
                    <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <Text>{d.name}</Text>
                      <Tag color={expiryTagColor(days)}>
                        {formatDate(d.license_expiry)} ({t('dashboard.daysSuffix', { count: days })})
                      </Tag>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Empty description={t('dashboard.noExpiringLicenses')} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '8px 0' }} />
            )}
          </Col>
          <Col xs={24} md={12}>
            <Text strong>
              {t('dashboard.vehicles')}{' '}
              <Link onClick={() => navigate('/vehicles')} style={{ fontSize: 12 }}>
                {t('dashboard.viewAll')}
              </Link>
            </Text>
            {expiringVehicles.length > 0 ? (
              <div style={{ marginTop: 8 }}>
                {expiringVehicles.map((v) => {
                  const days = daysUntilExpiry(v.registration_expiry);
                  return (
                    <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <Text>{v.fleet_number}</Text>
                      <Tag color={expiryTagColor(days)}>
                        {formatDate(v.registration_expiry)} ({t('dashboard.daysSuffix', { count: days })})
                      </Tag>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Empty description={t('dashboard.noExpiringRegistrations')} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '8px 0' }} />
            )}
          </Col>
        </Row>
      </Card>

      {/* 图表区域 */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        {/* 行程趋势图 */}
        <Col xs={24} lg={16} xl={16} xxl={16}>
          <Card title={t('dashboard.tripTrends')} style={{ borderRadius: 12, height: '100%' }}>
            {weeklyTrips.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={weeklyTrips}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="scheduled"
                    stroke={CHART_COLORS.primary}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 6 }}
                    name={t('dashboard.scheduled')}
                  />
                  <Line
                    type="monotone"
                    dataKey="completed"
                    stroke={CHART_COLORS.success}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 6 }}
                    name={t('dashboard.completed')}
                  />
                  <Line
                    type="monotone"
                    dataKey="cancelled"
                    stroke={CHART_COLORS.error}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ r: 3 }}
                    activeDot={{ r: 6 }}
                    name={t('dashboard.cancelled')}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Empty description={t('dashboard.noTripData')} />
            )}
          </Card>
        </Col>

        {/* 车辆状态饼图 */}
        <Col xs={24} md={12} lg={8} xl={8} xxl={8}>
          <Card title={t('dashboard.vehicleStatus')} style={{ borderRadius: 12, height: '100%' }}>
            {vehicleStatus.some(v => v.value > 0) ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={vehicleStatus}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {vehicleStatus.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 8 }}>
                  {vehicleStatus.map((item, index) => (
                    <div key={item.name} style={{ textAlign: 'center' }}>
                      <div style={{
                        width: 12, height: 12, borderRadius: '50%',
                        backgroundColor: PIE_COLORS[index % PIE_COLORS.length],
                        display: 'inline-block', marginRight: 6
                      }} />
                      <Text type="secondary">{item.name}: </Text>
                      <Text strong>{item.value}</Text>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <Empty description={t('dashboard.noVehicleData')} />
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {/* 本周行程状态柱状图 */}
        <Col xs={24} md={12}>
          <Card title={t('dashboard.weekTripStatus')} style={{ borderRadius: 12 }}>
            {tripStatus.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={tripStatus} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={30}>
                    {tripStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description={t('dashboard.noTripStatusData')} />
            )}
          </Card>
        </Col>

        {/* 快速操作 */}
        <Col xs={24} md={12}>
          <Card title={t('dashboard.quickActions')} style={{ borderRadius: 12 }}>
            <Row gutter={[12, 12]}>
              {[
                { name: t('dashboard.createTrip'), path: '/trips', color: CHART_COLORS.primary, icon: <FileTextOutlined />, desc: t('dashboard.createTripDesc') },
                { name: t('dashboard.addVehicle'), path: '/vehicles', color: CHART_COLORS.success, icon: <CarOutlined />, desc: t('dashboard.addVehicleDesc') },
                { name: t('dashboard.addDriver'), path: '/drivers', color: CHART_COLORS.info, icon: <UserOutlined />, desc: t('dashboard.addDriverDesc') },
                { name: t('dashboard.dataImportExport'), path: '/data-management', color: CHART_COLORS.warning, icon: <FileTextOutlined />, desc: t('dashboard.dataImportExportDesc') },
                { name: t('dashboard.settings'), path: '/settings', color: CHART_COLORS.purple, icon: <AlertOutlined />, desc: t('dashboard.settingsDesc') },
                { name: t('dashboard.viewSos'), path: '/sos-events', color: CHART_COLORS.error, icon: <AlertOutlined />, desc: t('dashboard.viewSosDesc') }
              ].map((action) => (
                <Col xs={24} sm={12} key={action.name}>
                  <Card
                    hoverable
                    size="small"
                    style={{ borderRadius: 8, borderLeft: `4px solid ${action.color}`, cursor: 'pointer' }}
                    onClick={() => navigate(action.path)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ color: action.color, fontSize: 20 }}>{action.icon}</span>
                      <div>
                        <Text strong style={{ fontSize: 14 }}>{action.name}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>{action.desc}</Text>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Dashboard;
